import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useForm from '../../hooks/useForm';
import { validateEmail, validateRequired, validateMobile, validatePassword } from '../../utils/validators';
import FormField from '../common/FormField';
import Alert from '../common/Alert';
import { ROLES } from '../../utils/constants';
import './RegisterForm.css';

const RegisterForm = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitError, setSubmitError] = useState('');

  const validate = (vals) => {
    const errs = {};
    errs.firstName = validateRequired(vals.firstName, 'First Name');
    errs.lastName = validateRequired(vals.lastName, 'Last Name');
    errs.email = validateEmail(vals.email);
    errs.mobile = validateMobile(vals.mobile);
    errs.password = validatePassword(vals.password);
    
    if (vals.password !== vals.confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    if (!vals.termsAccepted) {
      errs.termsAccepted = 'You must accept the terms and conditions';
    }
    
    // Remove nulls
    Object.keys(errs).forEach(key => errs[key] == null && delete errs[key]);
    return errs;
  };

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    role: searchParams.get('role') === 'landlord' ? ROLES.OWNER : ROLES.APPLICANT,
    termsAccepted: false
  };

  const { values, errors, handleChange, handleSubmit, isSubmitting } = useForm(initialValues, validate);

  const onSubmit = async (vals) => {
    setSubmitError('');
    try {
      await register(vals);
      navigate('/login', { state: { message: 'Registration successful. Please login.' } });
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const roleOptions = [
    { value: ROLES.APPLICANT, label: 'Tenant Applicant' },
    { value: ROLES.OWNER, label: 'Landlord / Property Manager' }
  ];

  return (
    <form className="register-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {submitError && <Alert type="error" message={submitError} dismissible onClose={() => setSubmitError('')} />}
      
      <div className="register-form__row">
        <FormField label="First Name" name="firstName" value={values.firstName} onChange={handleChange} error={errors.firstName} required />
        <FormField label="Last Name" name="lastName" value={values.lastName} onChange={handleChange} error={errors.lastName} required />
      </div>

      <FormField label="Email Address" name="email" type="email" value={values.email} onChange={handleChange} error={errors.email} required />
      <FormField label="Mobile Number" name="mobile" type="tel" value={values.mobile} onChange={handleChange} error={errors.mobile} placeholder="0821234567" required />
      
      <FormField label="I am a..." name="role" type="select" options={roleOptions} value={values.role} onChange={handleChange} required />

      <div className="register-form__row">
        <FormField label="Password" name="password" type="password" value={values.password} onChange={handleChange} error={errors.password} required />
        <FormField label="Confirm Password" name="confirmPassword" type="password" value={values.confirmPassword} onChange={handleChange} error={errors.confirmPassword} required />
      </div>

      <div className="register-form__terms">
        <label className={`terms-label ${errors.termsAccepted ? 'terms-label--error' : ''}`}>
          <input type="checkbox" name="termsAccepted" checked={values.termsAccepted} onChange={handleChange} required />
          <span>I accept the <Link to="/terms" target="_blank">Terms and Conditions</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link></span>
        </label>
        {errors.termsAccepted && <span className="terms-error">{errors.termsAccepted}</span>}
      </div>

      <button type="submit" className="register-form__submit" disabled={isSubmitting}>
        {isSubmitting ? 'Registering...' : 'Create Account'}
      </button>

      <p className="register-form__login-link">
        Already have an account? <Link to="/login">Login here</Link>
      </p>
    </form>
  );
};

export default RegisterForm;
